<template>
  <div v-loading="loading">

    <el-alert
      v-if="errored"
      type="error"
      :closable="false"
      show-icon
      title="Couldn't load the rate versions"
      description="Please try again in a moment." />

    <template v-else-if="loaded">

      <!-- Rateless: no rate assigned -->
      <el-row v-if="isRateless">
        <el-col :span="24" align="center">
          <span class="text-muted">—</span>
          <div class="text-muted" style="font-size:12px;margin-top:4px;">No rate assigned to this sharer.</div>
        </el-col>
      </el-row>

      <!-- Has versions (pinned or unpinned) -->
      <template v-else>

        <!-- Unpinned: no pinned version -->
        <template v-if="isUnpinned">

          <!-- unpinnedFallback=true + a current published version exists: show tiers -->
          <div v-if="unpinnedFallback && currentVersion">
            <div class="rv-header">
              <span>Version <strong>v{{ currentVersion.version_no }}</strong></span>
              <el-tag type="success" size="small">Current</el-tag>
              <span class="text-muted">Published {{ formatDate(currentVersion.published_at) }}</span>
            </div>
            <div v-if="currentVersion.name" class="text-muted" style="font-size:12px;">{{ currentVersion.name }}</div>
            <rate-tiers-cell :row-data="{ tiers: currentVersion.tiers || [] }" hide-drawer-affordance />
            <div class="text-muted" style="font-size:12px;margin-top:4px;">This sharer isn't pinned — showing the current published version (v{{ currentVersion.version_no }}).</div>
            <div class="text-muted rv-lg-hint">Rates #4&#8211;#6 shown on wider screens.</div>
          </div>

          <!-- unpinnedFallback=true + zero published versions: unavailable message -->
          <el-row v-else-if="unpinnedFallback && !currentVersion">
            <el-col :span="24" align="center">
              <div class="text-muted" style="font-size:13px;">Tier rates are unavailable for this rate.</div>
            </el-col>
          </el-row>

          <!-- default (unpinnedFallback=false): existing "Latest (unversioned)" display -->
          <el-row v-else>
            <el-col :span="24" align="center">
              <i class="fa fa-link text-muted" aria-hidden="true"></i> <i class="text-muted">Latest (unversioned)</i>
              <div class="text-muted" style="font-size:12px;margin-top:4px;">This sharer isn't pinned to a published rate version; its fees follow the current rate.</div>
            </el-col>
          </el-row>

        </template>

        <!-- Pinned: show version header + tiers -->
        <div v-else-if="isPinned">
          <div class="rv-header">
            <span>Version <strong>{{ versionLabel }}</strong></span>
            <el-tag :type="pinnedVersion.is_current ? 'success' : 'info'" size="small">{{ pinnedVersion.is_current ? 'Current' : 'Superseded' }}</el-tag>
            <span class="text-muted">Published {{ publishedLabel }}</span>
          </div>
          <div v-if="pinnedVersion && pinnedVersion.name" class="text-muted" style="font-size:12px;">{{ pinnedVersion.name }}</div>
          <rate-tiers-cell :row-data="tierRowData" hide-drawer-affordance />
          <div class="text-muted rv-lg-hint">Rates #4&#8211;#6 shown on wider screens.</div>
        </div>

      </template>

    </template>

  </div>
</template>

<script>
import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'

import RateTiersCell from '@/components/finance/rates/RateTiersCell'

export default {
  name: 'sharer-rate-version',

  mixins: [NumberMixins, PostMixins, DateMixins],

  components: {
    RateTiersCell
  },

  props: {
    id: {
      type: [Number, String],
      required: true
    },

    rateId: {
      type: [Number, String],
      default: null
    },

    // When true and the licensee is unpinned-but-rated, show the is_current
    // published version's tiers instead of the default "Latest (unversioned)"
    // message. Default false — keeps per-site FinanceSharer display unchanged.
    unpinnedFallback: {
      type: Boolean,
      default: false
    }
  },

  data: function () {
    return {
      versions: [],
      loading: false,
      loaded: false,
      errored: false
    }
  },

  computed: {
    pinnedVersion: function () {
      return this.versions.find(function (v) { return v.is_pinned }) || null
    },

    // The is_current published version from the fetched list — used by the
    // unpinned-fallback branch to show the current published version's tiers.
    currentVersion: function () {
      return this.versions.find(function (v) { return v.is_current }) || null
    },

    isPinned: function () {
      return this.loaded && !this.errored && !!this.pinnedVersion
    },

    isUnpinned: function () {
      return this.loaded && !this.errored && !!this.rateId && !this.pinnedVersion
    },

    isRateless: function () {
      return this.loaded && !this.errored && !this.rateId
    },

    versionLabel: function () {
      return this.pinnedVersion ? 'v' + this.pinnedVersion.version_no : ''
    },

    publishedLabel: function () {
      return (this.pinnedVersion && this.pinnedVersion.published_at)
        ? this.formatDate(this.pinnedVersion.published_at)
        : '—'
    },

    tierRowData: function () {
      return { tiers: (this.pinnedVersion && this.pinnedVersion.tiers) ? this.pinnedVersion.tiers : [] }
    }
  },

  watch: {
    rateId: function () {
      this.load()
    }
  },

  created: function () {
    this.load()
  },

  methods: {
    load: function () {
      if (!this.id) {
        this.loaded = true
        return
      }

      var self = this
      this.loading = true
      this.errored = false
      this.loaded = false

      this.fetchData(
        'licensees/' + this.id + '/rate-versions',
        function (data) {
          self.versions = Array.isArray(data) ? data : []
          self.loading = false
          self.loaded = true
        },
        'rate versions',
        function (_err) {
          self.errored = true
          self.loading = false
          self.loaded = true
        }
      )
    }
  }
}
</script>

<style scoped>
.rv-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}

.rv-lg-hint {
  font-size: 11px;
  margin-top: 4px;
}

@media (min-width: 1200px) {
  .rv-lg-hint { display: none; }
}
</style>
